// Mukti Hospital ERP - Pathology Outbox (Phase 2b)
// NEW FILE: src/services/pathologyOutbox.js
//

import pathologyDb from './pathologyDb';

/**
 * Add a new offline bill to the outbox + localBills.
 * @param {object} payload      the exact same payload the online /add uses
 * @param {string} clientId     a unique id (UUID) for this bill
 * @param {string} provisionalBillNumber  e.g. "MH_PB_OFF_a3f9"
 * @param {object} meta         { userName } who created it
 * @returns {Promise<boolean>}  true if queued successfully
 */
export const queueOfflineBill = async (payload, clientId, provisionalBillNumber, meta = {}) => {
  try {
    const createdAt = new Date().toISOString();

    await pathologyDb.outbox.add({
      type: 'bill',
      clientId,
      payload: { ...payload, clientId },
      userName: meta.userName || payload.userName || 'Unknown',
      createdAt,
      status: 'pending',
    });

    // Keep a viewable copy for the bill list
    await pathologyDb.localBills.put({
      clientId,
      provisionalBillNumber,
      payload: { ...payload, clientId },
      createdAt,
      syncedBillNumber: null,
    });

    return true;
  } catch (err) {
    console.error('[Outbox] Failed to queue offline bill:', err);
    return false;
  }
};

/**
 * Queue an offline pathology TEST creation. Same outbox table as bills,
 * differentiated by type: 'test'. Picked up by the sync engine later.
 */
export const queueOfflineTest = async (payload, clientId, meta = {}) => {
  try {
    const createdAt = new Date().toISOString();
    await pathologyDb.outbox.add({
      type: 'test',
      clientId,
      payload: { ...payload, clientId },
      userName: meta.userName || 'Unknown',
      createdAt,
      status: 'pending',
    });
    return true;
  } catch (err) {
    console.error('[Outbox] Failed to queue offline test:', err);
    return false;
  }
};

/** Get pending offline-created tests (not yet synced). */
export const getPendingTests = async () => {
  try {
    const all = await pathologyDb.outbox.where('status').equals('pending').toArray();
    return all.filter((item) => item.type === 'test');
  } catch (err) {
    console.error('[Outbox] Failed to read pending tests:', err);
    return [];
  }
};

/**
 * Queue an offline pathology AGENT creation. Same outbox table as bills/tests,
 * differentiated by type: 'agent'. Picked up by the sync engine later.
 */
export const queueOfflineAgent = async (payload, clientId, meta = {}) => {
  try {
    const createdAt = new Date().toISOString();
    await pathologyDb.outbox.add({
      type: 'agent',
      clientId,
      payload: { ...payload, clientId },
      userName: meta.userName || 'Unknown',
      createdAt,
      status: 'pending',
    });
    return true;
  } catch (err) {
    console.error('[Outbox] Failed to queue offline agent:', err);
    return false;
  }
};

/** Get pending offline-created agents (not yet synced). */
export const getPendingAgents = async () => {
  try {
    const all = await pathologyDb.outbox.where('status').equals('pending').toArray();
    return all.filter((item) => item.type === 'agent');
  } catch (err) {
    console.error('[Outbox] Failed to read pending agents:', err);
    return [];
  }
};

/**
 * Queue an offline pathology CATEGORY creation. Same outbox table as
 * bills/tests/agents, differentiated by type: 'category'.
 */
export const queueOfflineCategory = async (payload, clientId, meta = {}) => {
  try {
    const createdAt = new Date().toISOString();
    await pathologyDb.outbox.add({
      type: 'category',
      clientId,
      payload: { ...payload, clientId },
      userName: meta.userName || 'Unknown',
      createdAt,
      status: 'pending',
    });
    return true;
  } catch (err) {
    console.error('[Outbox] Failed to queue offline category:', err);
    return false;
  }
};

/** Get pending offline-created categories (not yet synced). */
export const getPendingCategories = async () => {
  try {
    const all = await pathologyDb.outbox.where('status').equals('pending').toArray();
    return all.filter((item) => item.type === 'category');
  } catch (err) {
    console.error('[Outbox] Failed to read pending categories:', err);
    return [];
  }
};

/**
 * Get ALL pending outbox items (bills + tests + future types), oldest first.
 * Used by the sync engine to drain the whole queue in one pass.
 */
export const getAllPending = async () => {
  try {
    return await pathologyDb.outbox
      .where('status')
      .equals('pending')
      .sortBy('createdAt');
  } catch (err) {
    console.error('[Outbox] Failed to read all pending:', err);
    return [];
  }
};

/** Get all pending outbox items, oldest first. */
export const getPendingBills = async () => {
  try {
    const all = await pathologyDb.outbox
      .where('status')
      .equals('pending')
      .sortBy('createdAt');
    return all.filter((item) => !item.type || item.type === 'bill');
  } catch (err) {
    console.error('[Outbox] Failed to read pending bills:', err);
    return [];
  }
};

/** How many bills are still waiting to sync. */
export const getPendingCount = async () => {
  try {
    const all = await pathologyDb.outbox.where('status').equals('pending').toArray();
    return all.filter((item) => !item.type || item.type === 'bill').length;
  } catch (err) {
    return 0;
  }
};

// A bill is given up on after this many failed server attempts.
export const MAX_SYNC_ATTEMPTS = 5;

/**
 * Record a failed sync attempt for a bill. Increments its attempt counter,
 * and once it reaches MAX_SYNC_ATTEMPTS marks it 'failed' so the engine
 * stops retrying it (and it no longer blocks the rest of the queue).
 * @returns {Promise<boolean>} true if the bill was marked 'failed'
 */
export const markBillFailed = async (outboxId) => {
  try {
    const item = await pathologyDb.outbox.get(outboxId);
    if (!item) return false;
    const attempts = (item.attempts || 0) + 1;
    const giveUp = attempts >= MAX_SYNC_ATTEMPTS;
    await pathologyDb.outbox.update(outboxId, {
      attempts,
      status: giveUp ? 'failed' : 'pending',
      lastError: item.lastError || null,
    });
    return giveUp;
  } catch (err) {
    console.error('[Outbox] Failed to record failed attempt:', err);
    return false;
  }
};

/** How many bills permanently failed to sync. */
export const getFailedCount = async () => {
  try {
    return await pathologyDb.outbox.where('status').equals('failed').count();
  } catch (err) {
    return 0;
  }
};

/** Get the local (offline-created) bills to show in the list. */

/** Get the local (offline-created) bills to show in the list. */
export const getLocalBills = async () => {
  try {
    return await pathologyDb.localBills.orderBy('createdAt').reverse().toArray();
  } catch (err) {
    console.error('[Outbox] Failed to read local bills:', err);
    return [];
  }
};

/**
 * Check whether this patient already has an UN-SYNCED offline bill on
 * this device, OR an online bill that was cached during this session.
 * Used to warn the staff before creating another one.
 * @param {function} getCachedBills  optional async () => array of cached online bills
 * @returns {Promise<object|null>} { provisionalBillNumber } of the match, or null
 */
export const findUnsyncedBillForPatient = async (
  patientId,
  patientName,
  userPhone,
  getCachedBills = null
) => {
  const matchesPatient = (p = {}) => {
    if (patientId && p.patientId) {
      return p.patientId === patientId;
    }
    if (patientName) {
      const sameName =
        (p.patientName || '').trim().toLowerCase() === patientName.trim().toLowerCase();
      const samePhone = userPhone ? (p.userPhone || '') === userPhone : true;
      return sameName && samePhone;
    }
    return false;
  };

  try {
    // 1) Check local offline (un-synced) bills
    const locals = await pathologyDb.localBills.toArray();
    const localMatch = locals.find(
      (lb) => !lb.syncedBillNumber && matchesPatient(lb.payload || {})
    );
    if (localMatch) {
      return { provisionalBillNumber: localMatch.provisionalBillNumber, source: 'offline' };
    }

    // 2) Check cached online bills (seen earlier this session), if provided
    if (typeof getCachedBills === 'function') {
      const cached = (await getCachedBills()) || [];
      const onlineMatch = cached.find((b) => matchesPatient(b));
      if (onlineMatch) {
        return { provisionalBillNumber: onlineMatch.billNumber, source: 'online' };
      }
    }

    return null;
  } catch (err) {
    console.error('[Outbox] Failed to check patient bills:', err);
    return null;
  }
};

export const markBillSynced = async (outboxId, clientId, realBillNumber) => {
  try {
    await pathologyDb.outbox.delete(outboxId);
    // Once synced, remove the local copy entirely. The real bill now comes
    // from the server, so keeping the local copy would only pile up in
    // IndexedDB over time. (realBillNumber is unused now but kept in the
    // signature so callers don't need to change.)
    await pathologyDb.localBills.delete(clientId);
  } catch (err) {
    console.error('[Outbox] Failed to mark bill synced:', err);
  }
};

/**
 * Mark an offline TEST as synced — just remove it from the outbox.
 * Tests don't have a separate local table, so this is simpler than bills.
 */
export const markTestSynced = async (outboxId) => {
  try {
    await pathologyDb.outbox.delete(outboxId);
  } catch (err) {
    console.error('[Outbox] Failed to mark test synced:', err);
  }
};

/**
 * Mark an offline AGENT as synced — just remove it from the outbox.
 * Same pattern as markTestSynced.
 */
export const markAgentSynced = async (outboxId) => {
  try {
    await pathologyDb.outbox.delete(outboxId);
  } catch (err) {
    console.error('[Outbox] Failed to mark agent synced:', err);
  }
};

/**
 * Mark an offline CATEGORY as synced — just remove it from the outbox.
 * Same pattern as markTestSynced.
 */
export const markCategorySynced = async (outboxId) => {
  try {
    await pathologyDb.outbox.delete(outboxId);
  } catch (err) {
    console.error('[Outbox] Failed to mark category synced:', err);
  }
};

/** Remove a fully-synced bill from the local list (optional cleanup). */
export const removeLocalBill = async (clientId) => {
  try {
    await pathologyDb.localBills.delete(clientId);
  } catch (err) {
    console.error('[Outbox] Failed to remove local bill:', err);
  }
};

// ---------------------------------------------------------------------------
// Offline agent id -> real id mapping
// ---------------------------------------------------------------------------
// When the user creates a new agent offline and immediately uses it on a
// bill, the bill stores agentId = "offline-<clientId>". The server has no
// idea what that is. After the agent itself successfully syncs, we record
// the real server-assigned id (e.g. "PA42") here. Right before pushing a
// bill, the sync engine looks up its agentId in this map and swaps the
// offline placeholder for the real id, so the bill writes against an
// existing agentId on the server.

const AGENT_ID_MAP_KEY = 'mukti_offline_agent_id_map';

const readAgentIdMap = () => {
  try {
    const raw = localStorage.getItem(AGENT_ID_MAP_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (err) {
    return {};
  }
};

const writeAgentIdMap = (map) => {
  try {
    localStorage.setItem(AGENT_ID_MAP_KEY, JSON.stringify(map));
  } catch (err) {
    /* storage full / disabled - safe to ignore */
  }
};

/** Record: an offline agent ("offline-<clientId>") now lives on the server as realId. */
export const rememberAgentIdMapping = (clientId, realId) => {
  if (!clientId || !realId) return;
  const map = readAgentIdMap();
  map[`offline-${clientId}`] = realId;
  writeAgentIdMap(map);
};

/** Look up the real server id for an offline agent placeholder, or null. */
export const resolveAgentId = (offlineId) => {
  if (!offlineId || typeof offlineId !== 'string') return null;
  if (!offlineId.startsWith('offline-')) return null;
  const map = readAgentIdMap();
  return map[offlineId] || null;
};
